import { isEqual } from 'es-toolkit/compat';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Location, Navigate, useLocation } from 'react-router-dom';

import { useGetIsTokenExpired, useGetUser, useIsPayee, useIsPayor } from 'app/api/hooks/core/v1/auth/user';
import { images } from 'app/constants/images';
import { FinancialAccountType } from 'app/constants/reports';
import {
  defaultPayeeQueryParams,
  defaultPayorQueryParams,
  payeeRoutes,
  payorRoutes,
  routes,
} from 'app/constants/routes';
import { buildLoginLink } from 'app/utils/lib/routing';

import ErrorPageUI from './ErrorPageUI';

// Local Typings
interface Props {
  withNavBar?: boolean;
}
interface RedirectRoute {
  generatePathname: (location: Location) => string;
  pathname: string;
}

// Local Variables
const loginRegisterPath = '/login/register';

function parsePath({
  basePath,
  legacyPathMatch,
  location,
}: {
  basePath: string;
  legacyPathMatch: string;
  location: Location;
}): string {
  const incomingPath = location.pathname.toLowerCase();
  const splitIncomingPath = incomingPath.split(legacyPathMatch);
  const endPath = splitIncomingPath[1];
  const newPath = endPath ? `${basePath}${endPath}` : basePath;

  return `${newPath}${location.search}`;
}

function getRedirectRoutesForUser({
  isPayee,
  isPayor,
}: {
  isPayee: boolean;
  isPayor: boolean;
}): RedirectRoute[] {
  /* eslint-disable @stylistic/max-len */
  const oldSitePayorRedirects: RedirectRoute[] = [
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.profile}`,
      pathname: '/account',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.profile}`,
      pathname: '/account/edit',
    },
    {
      generatePathname: () => '/',
      pathname: loginRegisterPath,
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.relationships}${defaultPayorQueryParams.relationships}`,
      pathname: '/payoraccounts',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.creditNotes}${defaultPayorQueryParams.credits}`,
      pathname: '/payorcreditnotes',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.dashboard}`,
      pathname: '/payorhome',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.invoices}${defaultPayorQueryParams.invoices}`,
      pathname: '/payorinvoices',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.loads}${defaultPayorQueryParams.loads}`,
      pathname: '/payorloads',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.payments}${defaultPayorQueryParams.payments}`,
      pathname: '/payorpayouts',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.billingReport}${defaultPayorQueryParams.billingReport}`,
      pathname: '/payorreport/billingreport',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.creditLimitReport}${defaultPayorQueryParams.creditLimitReport}`,
      pathname: '/payorreport/financialaccountreport/11',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.revenueShare}${defaultPayorQueryParams.revenueShare}`,
      pathname: '/payorreport/financialaccountreport/20',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.invoiceRefundsReport}${defaultPayorQueryParams.invoiceRefundsReport}`,
      pathname: `/payorreport/financialaccountreport/${FinancialAccountType.PayorRefunds}`,
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.previousSettlements}${defaultPayorQueryParams.settlements}`,
      pathname: '/payorreport/settlementstatementindex',
    },
    {
      generatePathname: () => `/${routes.payor}/${payorRoutes.settlements}${defaultPayorQueryParams.settlements}`,
      pathname: '/payor/paperwork',
    },
    {
      generatePathname: location => parsePath({
        basePath: `/${routes.payor}/${payorRoutes.settlements}`,
        legacyPathMatch: '/payor/paperwork',
        location,
      }),
      pathname: '/payor/paperwork/*',
    },
  ];
  const oldSitePayeeRedirects: RedirectRoute[] = [
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.profile}`,
      pathname: '/account',
    },
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.profile}`,
      pathname: '/account/edit',
    },
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.dashboard}`,
      pathname: loginRegisterPath,
    },
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.dashboard}`,
      pathname: '/payeehome',
    },
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.invoices}${defaultPayeeQueryParams.invoicesAndCreditNotes}`,
      pathname: '/payeeinvoices',
    },
    {
      generatePathname: () => `/${routes.payee}/${payeeRoutes.payments}${defaultPayeeQueryParams.payments}`,
      pathname: '/payeepayouts',
    },
  ];
  const oldSiteUnAuthenticatedRedirects: RedirectRoute[] = [
    {
      generatePathname: () => '/',
      pathname: loginRegisterPath,
    },
    {
      generatePathname: () => `/${routes.registerPayee}`,
      pathname: '/login/registerpayee',
    },
  ];
  /* eslint-enable @stylistic/max-len */

  if (isPayee) {
    return oldSitePayeeRedirects;
  }

  if (isPayor) {
    return oldSitePayorRedirects;
  }

  return oldSiteUnAuthenticatedRedirects;
}

const getRedirectRoute = ({
  location,
  redirects,
}: {
  location: Location;
  redirects: RedirectRoute[];
}): string | undefined => {
  const splitPathname = location.pathname.toLowerCase().split('/');

  const matchingRedirect = redirects.find(redirect => {
    const splitRedirectPathname = redirect.pathname.split('/');

    if (isEqual(splitRedirectPathname,
      splitPathname)) {
      return true;
    }

    const { [splitRedirectPathname.length - 1]: last } = splitRedirectPathname;

    if (last === '*') {
      const endIndex = splitRedirectPathname.length - 1;

      const splitRedirectPathnameStart = splitRedirectPathname.slice(0,
        endIndex);
      const splitPathnameStart = splitPathname.slice(0,
        endIndex);

      return isEqual(splitRedirectPathnameStart,
        splitPathnameStart);
    }
  });

  return matchingRedirect?.generatePathname(location);
};

// Component Definition
const FourOhFour: FC<Props> = props => {
  const { t } = useTranslation();

  const location = useLocation();

  const returnUrl = buildLoginLink(location);

  const user = useGetUser();
  const isPayee = useIsPayee();
  const isPayor = useIsPayor();
  const isTokenExpired = useGetIsTokenExpired();
  const redirectRoutes = getRedirectRoutesForUser({
    isPayee,
    isPayor,
  });

  const redirectRoute = getRedirectRoute({
    location,
    redirects: redirectRoutes,
  });

  if (redirectRoute) {
    return (
      <Navigate to={redirectRoute} />
    );
  }

  return !isTokenExpired && user
    ? (
      <ErrorPageUI
        alt="Stop Sign"
        backgroundImage={images.stopSign.full}
        backgroundImageLoader={images.stopSign.compressed}
        heading={t('common:fourOhFour.heading', 'We\'re sorry!')}
        linkTitle={t('common:returnToHome', 'Return to home')}
        // eslint-disable-next-line @stylistic/max-len
        subheading={t('common:fourOhFour.subheading', 'We couldn\'t connect you to the page you were looking for. Don\'t worry, you can navigate to the dashboard.')}
        withNavBar={props.withNavBar}
      />
    )
    : <Navigate to={returnUrl} />;
};

export default FourOhFour;
